/**
 * Pure helpers for classifying authentication failures, shared between the
 * MSAL/token layer (user store) and the HTTP layer (apiCall). Keeping these
 * here means "what counts as an auth failure" is defined in exactly one place.
 */
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';

/** Single dedupe key so only ONE "session expired" prompt is ever shown at a time. */
export const AUTH_NOTIFICATION_KEY = 'auth-session-expired';
export const FORBIDDEN_NOTIFICATION_KEY = 'auth-forbidden';
/**
 * Separate key from AUTH_NOTIFICATION_KEY: losing the 125 platform token costs
 * the 125 section only, while the depot side carries on. Sharing the session-
 * expired key would let one replace the other, and the two are not the same
 * problem or the same call to action.
 */
export const PLATFORM_NOTIFICATION_KEY = 'auth-125-unavailable';

/** User-facing copy, centralized so messaging stays consistent. */
export const AUTH_MESSAGES = {
	sessionExpiredTitle: 'Session expired',
	sessionExpiredBody:
		'Your session has expired or sign in is required. Please sign in again to continue.',
	reauthSuccessTitle: 'Signed back in',
	reauthSuccessBody: 'You can carry on where you left off.',
	reauthFailedTitle: 'Sign in required',
	reauthFailedBody: 'We could not refresh your session. Please sign in again to continue.',
	forbiddenTitle: 'Access denied',
	forbiddenBody: "You don't have permission to perform this action.",
	loginFailedTitle: 'Sign in failed',
	loginFailedBody: 'We could not sign you in. Please try again.',
	signInAgainCta: 'Sign in again',
	platformUnavailableTitle: '125 data unavailable',
	platformUnavailableBody:
		'We could not confirm your access to the 125 section. The depot section is unaffected.',
} as const;

/**
 * MSAL error codes that mean "silent refresh can't recover — the user must
 * interact" (consent, MFA, expired/revoked refresh token, password change, etc.).
 */
const INTERACTION_REQUIRED_CODES = new Set([
	'interaction_required',
	'login_required',
	'consent_required',
	'token_renewal_error',
	'no_tokens_found',
	'no_account_error',
	'monitor_window_timeout',
]);

/** True when the error means we must launch an interactive re-auth flow. */
export const isInteractionRequired = (error: unknown): boolean => {
	if (error instanceof InteractionRequiredAuthError) {
		return true;
	}

	const code = getErrorCode(error);
	if (code && INTERACTION_REQUIRED_CODES.has(code)) {
		return true;
	}

	// A cleared/expired browser session surfaces as a BrowserAuthError in some flows.
	if (error instanceof BrowserAuthError && code === 'no_account_error') {
		return true;
	}

	return false;
};

/** True when the user explicitly dismissed the popup — not a real failure. */
export const isUserCancellation = (error: unknown): boolean => {
	const code = getErrorCode(error);
	return code === 'user_cancelled' || code === 'access_denied';
};

export const getErrorCode = (error: unknown): string | undefined => {
	if (!error || typeof error !== 'object') {
		return undefined;
	}

	return (error as { errorCode?: string }).errorCode;
};

/** Extracts an HTTP status code from an axios error, a Response, or a plain object. */
export const getHttpStatus = (error: unknown): number | undefined => {
	if (!error || typeof error !== 'object') {
		return undefined;
	}

	const candidate = error as {
		status?: number;
		response?: { status?: number };
	};

	return candidate.response?.status ?? candidate.status;
};

export const isUnauthorized = (status: number | undefined): boolean => {
	return status === 401;
};

export const isForbidden = (status: number | undefined): boolean => {
	return status === 403;
};
